import React, { useState } from 'react'
import { useMutation } from '@apollo/client'
import { Box, Button, Typography, useMediaQuery, useTheme } from '@mui/material'

import surveyLogo from '../../assets/imgs/surveyLogo.svg'
import SAVE_LEADS from '../../graphqls/saveLeads.gql'
import useGetDynamicToken from '../../hooks/useGetDynamicToken'
import { LookingForHousingStep, MoveTimeStep, BudgetStep, FloorplanStep, ContactInfoStep } from './steps'
import * as Types from './types'

type FormData = Types.FormData
type SurveyState = Types.SurveyState

const BerkeleyHousingFairSurvey: React.FC = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const { getHeaderWithDynamicToken, signatureKeyLoading } = useGetDynamicToken()

  const [saveLeads, { loading: saveLeadsLoading }] = useMutation(SAVE_LEADS, {
    onCompleted: data => {
      console.log('Save leads success:', data)
      setState((prev: SurveyState) => ({
        ...prev,
        isSubmitted: true,
      }))
    },
    onError: error => {
      console.error('Save leads error:', error)
      // TODO: Show error message to user
    },
  })

  // Combined loading state for submit button
  const isSubmitting = saveLeadsLoading || signatureKeyLoading

  const [state, setState] = useState<SurveyState>({
    currentStep: 0,
    totalSteps: 5,
    isSubmitted: false,
    errors: {},
    formData: {
      name: '',
      email: '',
      phone: '',
      lookingForHousing: '',
      moveTime: '',
      budget: '',
      floorplan: '',
    },
  })

  const clearError = (stepIndex: number) => {
    setState((prev: SurveyState) => ({
      ...prev,
      errors: {
        ...prev.errors,
        [stepIndex]: undefined,
      },
    }))
  }

  const getStepError = (index: number) => {
    const { formData } = state
    switch (index) {
      case 0: // Looking for housing
        return formData.lookingForHousing ? null : true
      case 1: // Move Time (optional)
        return null
      case 2: // Budget (optional)
        return null
      case 3: // Floorplan (optional)
        return null
      case 4: // Contact Info
        const nameValid = formData.name.trim().length > 0
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        const emailValid = formData.email.trim().length === 0 || emailRegex.test(formData.email.trim())
        const phoneRegex = /^(\+?1[-. ]?)?\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/
        const phoneValid = phoneRegex.test(formData.phone.trim())

        if (!nameValid || !emailValid || !phoneValid) {
          return {
            name: !nameValid,
            email: !emailValid,
            phone: !phoneValid,
          }
        }
        return null
      default:
        return null
    }
  }

  const handleNext = () => {
    const error = getStepError(state.currentStep)
    if (error) {
      setState((prev: SurveyState) => ({
        ...prev,
        errors: { ...prev.errors, [prev.currentStep]: error },
      }))
      return
    }
    if (state.currentStep < state.totalSteps - 1) {
      setState((prev: SurveyState) => ({
        ...prev,
        currentStep: prev.currentStep + 1,
      }))
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  const handleBack = () => {
    if (state.currentStep > 0) {
      setState((prev: SurveyState) => ({
        ...prev,
        currentStep: prev.currentStep - 1,
      }))
    }
  }

  const updateFormData = (updates: Partial<FormData>) => {
    setState((prev: SurveyState) => ({
      ...prev,
      formData: { ...prev.formData, ...updates },
    }))
  }

  const handleContactChange = (updates: { name?: string; email?: string; phone?: string }) => {
    updateFormData(updates)
    const stepErrors = (state.errors[4] as any) || {}
    const newStepErrors = { ...stepErrors }
    if (updates.name) delete newStepErrors.name
    if (updates.email) delete newStepErrors.email
    if (updates.phone) delete newStepErrors.phone

    if (Object.keys(newStepErrors).length === 0) {
      clearError(4)
    } else {
      setState((prev: SurveyState) => ({
        ...prev,
        errors: { ...prev.errors, 4: newStepErrors },
      }))
    }
  }

  const handleLookingForHousingChange = (val: string) => {
    const wasComplete = !!state.formData.lookingForHousing
    updateFormData({ lookingForHousing: val })
    if (val) clearError(0)

    if (isMobile && !wasComplete && val) {
      setTimeout(() => {
        if (state.currentStep < state.totalSteps - 1) {
          setState((prev: SurveyState) => ({
            ...prev,
            currentStep: prev.currentStep + 1,
          }))
          window.scrollTo({ top: 0, behavior: 'smooth' })
        }
      }, 500)
    }
  }

  const handleMoveTimeChange = (val: string) => {
    updateFormData({ moveTime: val })
  }

  const handleBudgetChange = (val: string) => {
    updateFormData({ budget: val })
  }

  const handleFloorplanChange = (val: string) => {
    updateFormData({ floorplan: val })
  }

  const handleSubmit = async () => {
    let hasError = false
    const newErrors: Record<number, any> = {}

    for (let i = 0; i < state.totalSteps; i++) {
      const error = getStepError(i)
      if (error) {
        newErrors[i] = error
        hasError = true
      }
    }

    if (hasError) {
      setState((prev: SurveyState) => ({
        ...prev,
        errors: newErrors,
      }))
      const firstErrorIndex = Object.keys(newErrors)
        .map(Number)
        .sort((a, b) => a - b)[0]

      if (isMobile) {
        setState((prev: SurveyState) => ({
          ...prev,
          currentStep: firstErrorIndex,
        }))
      }
      return
    }

    // Process name field into firstName, middleName, and lastName
    const nameParts = state.formData.name
      .trim()
      .split(' ')
      .filter(part => part.length > 0)
    const firstName = nameParts[0] || ''
    const lastName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : ''
    const middleName = nameParts.length > 2 ? nameParts.slice(1, -1).join(' ') : ''

    // Create comment field from survey data (excluding contact info and name)
    const surveyNote = [
      `Source From: 20260422_berkeley_housing_fair`,
      `Looking for next year housing: ${state.formData.lookingForHousing}`,
      state.formData.moveTime ? `Move Time: ${state.formData.moveTime}` : '',
      state.formData.budget ? `Budget: ${state.formData.budget}` : '',
      state.formData.floorplan ? `Floorplan: ${state.formData.floorplan}` : '',
    ]
      .filter(Boolean)
      .join(' | ')

    // Generate required authentication parameters using dynamic token
    try {
      const { key, timestamp, signature } = await getHeaderWithDynamicToken()

      // Call saveLeads GraphQL mutation
      saveLeads({
        variables: {
          firstName,
          middleName: middleName || undefined,
          lastName,
          email: state.formData.email || undefined,
          phone: state.formData.phone,
          comment: surveyNote,
          sourceFrom: 'tripalink_website_leave_message',
          originalChannel: '20260422_berkeley_housing_fair',
          region: 'Berkeley',
          key,
          timestamp,
          signature,
        },
      })
    } catch (error) {
      console.error('Failed to generate dynamic token:', error)
      // TODO: Show error message to user
    }
  }

  const steps = [
    <LookingForHousingStep
      key="step1"
      value={state.formData.lookingForHousing}
      onChange={handleLookingForHousingChange}
      error={!!state.errors[0]}
    />,
    <MoveTimeStep key="step2" value={state.formData.moveTime} onChange={handleMoveTimeChange} />,
    <BudgetStep key="step3" value={state.formData.budget} onChange={handleBudgetChange} />,
    <FloorplanStep key="step4" value={state.formData.floorplan} onChange={handleFloorplanChange} />,
    <ContactInfoStep
      key="step5"
      name={state.formData.name}
      email={state.formData.email}
      phone={state.formData.phone}
      onChange={handleContactChange}
      errors={state.errors[4]}
    />,
  ]

  return (
    <div className="min-h-screen bg-[#f7f9fc] pb-20">
      {/* Header */}
      <div className="bg-[#111212] px-6 py-6 text-white shadow-md">
        <div className="mx-auto flex max-w-2xl flex-col items-start gap-4 md:flex-row md:items-center md:gap-3">
          <div className="flex items-center gap-4">
            <img src={surveyLogo} alt="Logo" className="h-6" />
            <div className="hidden h-6 w-[1px] bg-white/20 md:block" />
          </div>
          <h1 className="text-lg font-semibold tracking-wide">4/22 UC Berkeley Housing Fair Check-In</h1>
        </div>
      </div>

      <div className="mx-auto max-w-[670px] px-4 pt-10">
        {state.isSubmitted ? (
          <Box className="flex flex-col items-center justify-center pt-20 px-4">
            <Box className="bg-white p-8 rounded-2xl shadow-lg max-w-md w-full text-center">
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="lucide lucide-circle-check w-8 h-8 text-green-600"
                  aria-hidden="true"
                >
                  <circle cx="12" cy="12" r="10" />
                  <path d="m9 12 2 2 4-4" />
                </svg>
              </div>
              <Typography variant="h5" className="text-2xl font-semibold text-[#050a22] mb-2">
                All set!
              </Typography>
              <Typography variant="body1" className="text-gray-600">
                Your survey has been successfully submitted. We&apos;ve received your preferences.
              </Typography>
            </Box>
          </Box>
        ) : (
          <Box>
            {isMobile ? (
              // Mobile Wizard Layout
              <Box className="relative">
                {/* Progress Bar */}
                <Box className="mb-6 h-1 w-full rounded-full bg-gray-200">
                  <Box
                    className="h-full rounded-full bg-black transition-all duration-300"
                    sx={{
                      width: `${((state.currentStep + 1) / state.totalSteps) * 100}%`,
                    }}
                  />
                </Box>
                <Typography variant="body2" className="mb-4 text-gray-500">
                  Question {state.currentStep + 1} of {state.totalSteps}
                </Typography>

                <Box className="rounded-2xl bg-white p-6 shadow-lg border">{steps[state.currentStep]}</Box>

                {/* Navigation Buttons for Mobile */}
                <div className="mt-8 flex gap-4">
                  {state.currentStep > 0 && (
                    <Button
                      type="button"
                      variant="outlined"
                      onClick={handleBack}
                      className="flex-1"
                      sx={{
                        borderRadius: '12px',
                        borderColor: '#e0e2ef',
                        py: 1.5,
                        fontSize: '16px',
                        textTransform: 'none',
                        color: '#050a22',
                        '&:hover': {
                          borderColor: '#c1c7d0',
                          backgroundColor: 'rgba(5, 10, 34, 0.04)',
                        },
                      }}
                    >
                      Back
                    </Button>
                  )}
                  {state.currentStep < state.totalSteps - 1 ? (
                    <Button
                      type="button"
                      variant="contained"
                      onClick={handleNext}
                      className="flex-1"
                      sx={{
                        borderRadius: '12px',
                        backgroundColor: '#050a22',
                        py: 1.5,
                        fontSize: '16px',
                        textTransform: 'none',
                        '&:hover': {
                          backgroundColor: 'rgba(5, 10, 34, 0.9)',
                        },
                      }}
                    >
                      Next
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      variant="contained"
                      onClick={handleSubmit}
                      disabled={isSubmitting}
                      className="flex-1"
                      sx={{
                        borderRadius: '12px',
                        backgroundColor: '#e74f4f',
                        py: 1.5,
                        fontSize: '16px',
                        textTransform: 'none',
                        '&:hover': {
                          backgroundColor: '#d64444',
                        },
                        '&:disabled': {
                          backgroundColor: '#ccc',
                          color: '#666',
                        },
                      }}
                    >
                      {isSubmitting ? 'Submitting...' : 'Submit'}
                    </Button>
                  )}
                </div>
              </Box>
            ) : (
              // Desktop List Layout
              <Box className="bg-white p-12 rounded-2xl shadow-lg border">
                {steps.map((step, index) => (
                  <Box
                    key={index}
                    id={`step-${index}`}
                    className="border-b border-dashed border-gray-200 pb-12 mb-12 last:border-0 last:pb-0 last:mb-0"
                  >
                    {step}
                  </Box>
                ))}

                {/* Info Box */}
                <Box className="mt-8 p-4 bg-[#f0f4ff] rounded-lg border border-[#d0daf0]">
                  <Typography variant="body1" className="text-[#050a22] font-medium mb-2">
                    Swing by Bosco for our open house (Apr 22–May 17, weekdays 12–5 PM) and check out your next home!
                  </Typography>
                  <Typography variant="body2" className="text-gray-600">
                    <strong>Address:</strong> 1717 University Ave, Berkeley, CA 94703
                  </Typography>
                </Box>

                <div className="pt-8 flex justify-start" id="submit-section">
                  <Button
                    type="button"
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    sx={{
                      minWidth: 140,
                      px: 4,
                      py: 1.5,
                      backgroundColor: '#e74f4f',
                      borderRadius: '12px',
                      textTransform: 'none',
                      fontSize: '16px',
                      fontWeight: 600,
                      boxShadow: '0 10px 15px -3px rgba(231, 79, 79, 0.1), 0 4px 6px -2px rgba(231, 79, 79, 0.05)',
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: '#d64444',
                        boxShadow: '0 20px 25px -5px rgba(231, 79, 79, 0.1), 0 10px 10px -5px rgba(231, 79, 79, 0.04)',
                      },
                      '&:disabled': {
                        backgroundColor: '#ccc',
                        color: '#666',
                        cursor: 'not-allowed',
                        boxShadow: 'none',
                      },
                    }}
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit'}
                  </Button>
                </div>
              </Box>
            )}

            {/* Info Box for Mobile (outside the card) */}
            {isMobile && (
              <Box className="mt-6 p-4 bg-[#f0f4ff] rounded-lg border border-[#d0daf0]">
                <Typography variant="body1" className="text-[#050a22] font-medium mb-2">
                  Swing by Bosco for our open house (Apr 22–May 17, weekdays 12–5 PM) and check out your next home!
                </Typography>
                <Typography variant="body2" className="text-gray-600">
                  <strong>Address:</strong> 1717 University Ave, Berkeley, CA 94703
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </div>
    </div>
  )
}

export default BerkeleyHousingFairSurvey
